/**
 * Melee — the knife.
 *
 * This existed as a key binding and nothing else. `melee` was in `BINDINGS`, the
 * gamepad mapped it to the right stick click, and the touch overlay grew a button
 * for it, but no module ever asked `input.actionPressed('melee')`. Pressing it did
 * nothing, on every device, and had always done nothing. The overlay button was
 * built from the bindings table on the assumption that a binding implies a feature,
 * which is worth writing down as the mistake it was: a control that is wired to
 * nothing is worse than a missing control, because the player blames themselves.
 *
 * ## What a melee has to be
 *
 * A knife is a *lunge*, not a ray. Two properties make it feel right and neither is
 * optional:
 *
 *   - It has to forgive aim. A player who swings at someone a little off-centre
 *     expects to connect, so the test is a cone, not a line — anything inside
 *     `halfAngle` of straight ahead is fair game.
 *   - It has to respect walls. A cone alone will happily stab through a shipping
 *     container, so every candidate is confirmed with a line-of-sight probe before it
 *     takes damage.
 *
 * Nearest-first, one target per swing. A knife that cleaves a whole squad in one
 * animation is a knife nobody would ever put down.
 *
 * ## Why it lives outside WeaponSystem
 *
 * Because it is not a weapon: it does not consume ammunition, does not use the
 * ballistics path, does not care about attachments, and works identically whatever
 * is in the player's hands. It only borrows the weapon's *timeline* — the trigger is
 * inhibited for the length of the swing, through the same `fireInhibit` flag the
 * overheat lockout uses, so a swing cannot be cancelled into a shot.
 */

import * as THREE from 'three';
import { MEANS } from '../game/MatchAuthority.js';

export const TUNING = {
  /**
   * Damage. 90 against 100 health is deliberately not a one-shot: it is a two-hit
   * kill that leaves the victim one hit from death, so a knife that opens an
   * engagement is decisive without removing the fight.
   */
  damage: 90,
  /** Metres from the eye. A long lunge — this is a commitment, so it should reach. */
  range: 2.6,
  /** Half-angle of the forgiveness cone, radians. ~35 degrees. */
  halfAngle: 0.61,
  /** Seconds the whole swing takes, and how far into it the damage lands. */
  duration: 0.55,
  hitAt: 0.18,
  /** Seconds after the swing before another can start. */
  cooldown: 0.15,
};

const _fwd = new THREE.Vector3();
const _to = new THREE.Vector3();
const _eye = new THREE.Vector3();
const _hit = new THREE.Vector3();

export class Melee {
  constructor(ctx, player) {
    this.ctx = ctx;
    this.player = player;
    this.swinging = false;
    this.swings = 0;
    this.hits = 0;
    this._t = 0;
    this._struck = false;
    this._cooldown = 0;

    ctx.engine.registerUpdate((dt) => this.update(dt), 'sim');
  }

  update(dt) {
    if (this._cooldown > 0) this._cooldown -= dt;

    if (this.swinging) {
      this._t += dt;
      // The strike lands partway through, not at the start: the damage should
      // coincide with the blade being out, which is also what makes the swing
      // dodgeable rather than instant.
      if (!this._struck && this._t >= TUNING.hitAt) {
        this._struck = true;
        this._strike();
      }
      // Held for the whole swing, every frame, so nothing can leave the trigger
      // inhibited if this module stops running. Same contract as the overheat lock.
      const ws = this.ctx.weapons;
      if (ws) ws.fireInhibit = true;
      if (this._t >= TUNING.duration) {
        this.swinging = false;
        this._cooldown = TUNING.cooldown;
      }
      return;
    }

    if (this._cooldown > 0) return;
    if (this.player?.dead) return;
    if (!this.ctx.input?.actionPressed?.('melee')) return;
    this._begin();
  }

  _begin() {
    this.swinging = true;
    this.swings++;
    this._t = 0;
    this._struck = false;
    this.ctx.events.emit('weapon:melee', { stage: 'start', duration: TUNING.duration });
    this.ctx.events.emit('audio:play', { id: 'melee_swing', volume: 0.7 });
  }

  /** The eye ray for this swing, in world space. */
  _aim() {
    const cam = this.ctx.camera;
    cam.getWorldDirection(_fwd);
    _eye.setFromMatrixPosition(cam.matrixWorld);
    return _fwd;
  }

  _strike() {
    const fwd = this._aim();
    const target = this._pick(fwd);
    if (!target) {
      this.ctx.events.emit('weapon:melee', { stage: 'miss' });
      return;
    }

    this.hits++;
    _hit.copy(target.actor.position);
    _hit.y += 1.1; // chest-ish, so the blood and the hit marker land on a body

    const point = _hit.clone();
    const dir = fwd.clone();
    const normal = fwd.clone().negate();

    const res = this.ctx.match?.requestDamage({
      target: target.actor,
      source: this.player,
      amount: TUNING.damage,
      hitbox: null,
      point,
      normal,
      dir,
      means: MEANS.MELEE,
      weapon: 'melee',
      dist: target.dist,
    });
    if (res?.blocked) return;

    this.ctx.events.emit('hit:actor', {
      actor: target.actor,
      point,
      normal,
      damage: res?.amount ?? TUNING.damage,
      headshot: false,
      dir,
      hitbox: null,
      weapon: 'melee',
      source: this.player,
    });
    this.ctx.events.emit('weapon:melee', { stage: 'hit', actor: target.actor });
    this.ctx.events.emit('audio:play', {
      id: 'melee_hit',
      position: point.clone(),
      volume: 0.9,
    });
  }

  /**
   * Nearest living actor inside the cone with clear line of sight, or null.
   *
   * The sight probe is the part that stops this being a stab through walls, and it
   * is done last on purpose — it is the only expensive test here, so it runs on the
   * one candidate that has already passed range and angle.
   */
  _pick(fwd) {
    const list = this.ctx.ai?.enemies;
    if (!list?.length) return null;

    const cosLimit = Math.cos(TUNING.halfAngle);
    let best = null;

    for (const e of list) {
      if (!e || e.dead) continue;
      _to.copy(e.position);
      _to.y += 1.0;
      _to.sub(_eye);
      const dist = _to.length();
      if (dist > TUNING.range || dist < 1e-4) continue;
      _to.divideScalar(dist);
      if (_to.dot(fwd) < cosLimit) continue;
      if (!best || dist < best.dist) best = { actor: e, dist };
    }

    if (!best) return null;
    const physics = this.ctx.physics;
    if (physics?.visible) {
      _to.copy(best.actor.position);
      _to.y += 1.0;
      if (!physics.visible(_eye, _to, best.actor)) return null;
    }
    return best;
  }

  report() {
    return {
      swinging: this.swinging,
      swings: this.swings,
      hits: this.hits,
      damage: TUNING.damage,
      range: TUNING.range,
      coneDeg: +((TUNING.halfAngle * 180) / Math.PI).toFixed(1),
    };
  }
}
